import React from 'react';
import Container from 'components/Container';
import { H1 } from 'components/Typography';
import Status from 'components/Status';
import {
  Background,
  UpdatedOn,
  Section,
  Heading,
  Paragraph,
  Box,
  BoxColumn,
  List,
  Email,
  SubHeader
} from './styled';

const PrivacyPolicy = () => {
  return (
    <Container bg="white" variant="dark">
      <Background>
        <H1>SafetyWing Customer Privacy Policy</H1>
        <UpdatedOn>UPDATED ON SEPTEMBER 1, 2023</UpdatedOn>

        <Section>
          <Heading>1. Introduction</Heading>
          <Paragraph>
            At SafetyWing, we take the safe and confidential handling of your personal
            data seriously, and we want you to understand how we are doing this and make
            you fully aware of your rights.
          </Paragraph>
          <Paragraph>
            We have drafted our Privacy Policy to be simple, clear, and concise, so that
            we accurately describe how we are collecting and using the personal data we
            collect.
          </Paragraph>
          <Paragraph>
            Building a global social safety net is a lot of work, and requires us to
            collect some personal data from our customers. Through our Data Privacy
            controls, we take good care of all the personal data we collect.
          </Paragraph>
          <Paragraph>
            The overall approach we follow at SafetyWing is that:
            <ul>
              <li>
                We only process Personal Data when there is a valid reason (“lawfulness”).
              </li>
              <li>
                We are always transparent about our intentions with personal data
                (“transparency”).
              </li>
              <li>
                We only collect what we need and as much as we need, not a letter more
                (“purpose limitation” and “data minimisation”).
              </li>
              <li>
                We only keep data as long as we need it, then it is deleted (“storage
                limitation”).
              </li>
              <li>We keep all personal data secure.</li>
              <li>We make every effort to respect your data rights.</li>
            </ul>
            While we have put care and attention into making this policy as clear as
            possible, it's ok to have questions and we are here to help. Feel free to
            reach out to our customer support team or Data Protection Officer (
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email>) and they
            will be more than happy to help.
          </Paragraph>
        </Section>

        <Section>
          <Heading>2. Why do we need your data anyway? (Purpose of processing)</Heading>
          <Paragraph>That's a good question. Well, for a few reasons actually.</Paragraph>
          <Paragraph>
            We want to provide you with the best service possible, answer all your
            questions in a specific and useful way, make sure that our products and
            services are suitable for you, and ultimately to enter into a contract with
            you to provide that service. To do all this, we need your personal data.
          </Paragraph>
        </Section>

        <Section>
          <Heading>3. TL;DR (The short version)</Heading>
          <Paragraph>
            Privacy Policies are not always easy or fun to read, and we get that. So,
            here's a brief summary of what you want to know:
            <ul>
              <li>
                We only collect the Personal Data that we need to provide you with
                products and services.
              </li>
              <li>
                In most cases, we process your data based on a valid reason (“lawful
                basis”), contractual or legal obligations and therefore we do not need
                your consent.
              </li>
              <li>
                Personal Data is collected either directly from customers or from related
                third parties.
              </li>
              <li>
                Sensitive data (special categories of Personal Data) might be collected in
                specific situations, and will be handled with extra care.
              </li>
              <li>
                We usually keep the Personal Data of customers for 7 years following the
                expiration of their policies.
              </li>
              <li>
                There are international transfers of personal data, as you would expect
                from a global company like SafetyWing.
              </li>
              <li>You have rights!</li>
            </ul>
          </Paragraph>
        </Section>

        <Section>
          <Heading>4. A few definitions to make the reading easier</Heading>
          <Paragraph>
            <ul>
              <li>
                <b>Personal Data:</b> Any information that can identify a natural person
                (i.e. human)
              </li>
              <li>
                <b>(Personal Data) Processing:</b> Collection, recording, organization,
                structuring, storage, adaptation or alteration, retrieval, consultation,
                use, disclosure by transmission, dissemination or otherwise making
                available, alignment or combination, restriction, erasure or destruction
                of Personal Data.
              </li>
              <li>
                <b>Data Controller:</b> An organization that decides how the personal data
                it collects and holds is used and dictates how other companies or
                individuals (“data processors”) should process personal data.
              </li>
              <li>
                <b>Data Processor:</b> A person (natural or legal) that processes personal
                data of which it has no ownership according to the instructions provided
                by the Data Controller.
              </li>
              <li>
                <b>Special Categories of Personal Data:</b> Personal data revealing
                sensitive information including but not limited to genetic data, biometric
                data, data concerning health or data concerning a natural person's sex
                life or sexual orientation.
              </li>
              <li>
                <b>Lawful basis:</b> A valid, legitimate reason to process Personal Data.
              </li>
            </ul>
          </Paragraph>
        </Section>

        <Section>
          <Heading>5. Let's start with the what, why, and where</Heading>
          <Paragraph>
            <b>What</b> data do we collect, <b>why</b> do we collect it, and <b>where</b>{' '}
            do we get it from?
          </Paragraph>
          <Paragraph>
            We want to make sure that we know our customers well so that we can help them
            get the best experience with SafetyWing. To do this, we need to collect a few
            details about them, as presented below.
          </Paragraph>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                Identification details:
                <li>Full name</li>
                <li>Gender</li>
                <li>Address</li>
                <li>Date of birth</li>
                <li>Home country, citizenship, nationality</li>
                <li>Occupation</li>
                <li>
                  National identifier numbers (including passport number and/or driver's
                  license number)
                </li>
                <li>Email address and phone number</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <List>
                We only use this to identify you and:
                <li>To open and manage your accounts and relationship with us</li>
                <li>To verify your age</li>
                <li>To enhance your experience and our products and services</li>
                <li>
                  To share relevant marketing about our products and services, news and
                  updates, and upcoming events (if you consent to it)
                </li>
                <li>To meet our legal and regulatory obligations</li>
                <li>To enable you to register for an online account</li>
                <li>To contact you about your account(s)</li>
                <li>
                  To determine your eligibility for some of our products and services
                </li>
              </List>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                Family details:
                <li>Marital status</li>
                <li>Next of kin</li>
                <li>Dependents</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <Paragraph>
                To understand your circumstances and needs and assess the suitability of
                products and services you apply for with us or one of our partners.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Data on how you interact with us</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>Why do we need it</b>
              <Paragraph>
                To give you the best service and prevent fraud. This can include whether
                you have carried out a transaction over the phone or over the internet.
              </Paragraph>
            </BoxColumn>
          </Box>

          <Paragraph>
            <b>Where do we get it from</b>
          </Paragraph>
          <Paragraph>
            We get this data either directly from you or through third parties in cases
            where someone else adds you as a dependent or member of a policy that you do
            not own.
          </Paragraph>

          <Paragraph>
            More precisely, we will obtain data directly from you via:
          </Paragraph>

          <List>
            <li>
              <b>Registration & Purchase</b>
              <List>
                <li>
                  When you sign up for the first time and make your first purchase. That's
                  when we initially collect most of your personal data.
                </li>
                <li>
                  When you update your personal data online or you request that we make
                  changes. We process your personal data by updating them to be accurate.
                </li>
              </List>
            </li>
            <li>
              <b>Phone communications</b>
              <List>
                <li>
                  When you speak to us on the phone, we may record the call for training
                  and quality purposes.
                </li>
              </List>
            </li>
            <li>
              <b>Website and application use</b>
              <List>
                <li>
                  When using our websites, and any digital or mobile app we may offer now
                  or in the future.
                </li>
              </List>
            </li>
            <li>
              <b>Written communication</b>
              <List>
                <li>
                  When you reach out to us through our customer support chat services, the
                  conversation is stored in our records. When you send us emails, we store
                  those in our records as well.
                </li>
                <li>
                  When we use information that you've made public, such as social media
                  content or when you interact with our social media profiles.
                </li>
              </List>
            </li>
            <li>
              <b>Data collected from third parties</b>
              There are also times when personal data will come from a third party. For
              example:
              <List>
                <li>
                  If you are signing up as a joint account with someone else, and you are
                  not the main policy holder, we will collect your personal data when the
                  policy holder signs you up.
                </li>
                <li>
                  When a nominated representative signs you up. Since the signup will be
                  about you, we will collect your personal data.
                </li>
              </List>
            </li>
          </List>
        </Section>

        <Section>
          <Heading>
            6. You may be wondering on what grounds we are collecting Personal Data
            (“Lawfulness of processing”)
          </Heading>
          <Paragraph>
            We collect your data for a few reasons, and most of them do not require us to
            get consent from you. This means that a) we do not need to bother you with
            giving us consent and b) we really need this data to provide our Products and
            Services.
          </Paragraph>
          <Paragraph>
            According to data privacy frameworks, personal data can only be collected
            where there are valid reasons. In the absence of any other legal reason
            (lawful basis), we need to obtain your consent to process your data. These are
            the grounds on which we are collecting data from you (“lawful bases”):
          </Paragraph>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Consent</Paragraph>
              <Status variant="yellow" text="Less frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we ask you to confirm whether you are ok for us to process
                your personal data, <b>for a specific purpose</b>.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Explicit Consent</Paragraph>
              <Status variant="yellow" text="Less frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we process any of your sensitive information (special
                categories of personal data), where we <b>explicitly</b> obtain your
                consent for the processing.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Contract</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we need to process your personal data so that we can execute
                our contract with you to provide you the service. Unless we process the
                data, a contract cannot exist.
              </Paragraph>
              <Paragraph>In this circumstance, your consent is not required.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Legal Obligation</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when we need to process your personal data in order to comply with
                the law. Again, in this circumstance, no consent is required.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Lawful basis</b>
              <Paragraph>Legitimate Interest</Paragraph>
              <Status variant="purple" text="More frequently used" />
            </BoxColumn>
            <BoxColumn>
              <b>Description</b>
              <Paragraph>
                This is when processing is necessary for our legitimate interests or the
                legitimate interests of a third party, i.e., valid reasons. No consent is
                required from you here either.
              </Paragraph>
            </BoxColumn>
          </Box>
        </Section>

        <Section>
          <Heading>
            7. Does SafetyWing need sensitive personal data? (“Processing of Special
            Categories of Personal Data”)
          </Heading>

          <Paragraph>
            At SafetyWing, sensitive data usually means health data such as medical
            history, claims information, vaccination history, and any other information
            relevant to your coverage, policy or claim. If we need to collect or process
            any sensitive data, you can rest assured that we will let you know about this
            in advance. Not only will you know, but you will also be asked whether you
            approve of SafetyWing processing any personal data (“explicit consent”).
          </Paragraph>
          <Paragraph>
            In the event that you submit information to us which involves any sensitive
            data, thereby granting us access to sensitive data, we will ask for your
            consent to process the data, and will also handle them with additional
            security, as this is what the laws require us to do.
          </Paragraph>
        </Section>

        <Section>
          <Heading>8. What about personal data regarding children?</Heading>
          <Paragraph>
            Although we don’t offer products directly to children, we may happen to become
            recipients of such data when you inquire about a family policy and you wish
            for your children to be covered. When we do collect this information, we will
            only ask for the minimal amount of data to enable an accurate quotation for
            coverage. Any data, like all the information we store, will be protected and
            kept secure, and the only people able to access it will be those who strictly
            need to in order to help with your request.
          </Paragraph>
        </Section>

        <Section>
          <Heading>9. What's SafetyWing's role when processing my personal data?</Heading>
          <Paragraph>
            SafetyWing will always be the <b>Data Controller</b> for any personal data
            collected from customers. This means that we will be responsible for how your
            data is processed, including cases where we need to transfer your data to
            third parties.
          </Paragraph>
          <Paragraph>
            In other words, even when your data is transferred to third parties, we are
            still responsible for it.
          </Paragraph>
        </Section>

        <Section>
          <Heading>
            10. Why does SafetyWing give my Personal Data to third parties?! (“transfers
            to third parties & international transfers”)
          </Heading>
          <Paragraph>
            As the saying goes, it takes a village, and this is very true in building a
            global social safety net. In order to provide our Products and Services, we
            sometimes need to share your data with third parties, and this sometimes
            involves international transfers of data. Let's address two important
            questions:
          </Paragraph>
          <List>
            <li>Where does your personal data travel to?</li>
            <li>How do we protect your data through this process?</li>
          </List>
          <SubHeader>10.1. Let's start with where.</SubHeader>
          <Paragraph>
            Personal data is shared with the companies we use to provide, promote and
            protect our products, and our insurance partners who, in some cases, are the
            ones that enable us to provide innovative new products.
          </Paragraph>
          <Paragraph>
            In both cases, personal data might be shared internationally, as our vendors
            might have data hosted in different locations.
          </Paragraph>
          <List>
            Some examples of where we share your data to:
            <li>Our insurance partners</li>
            <li>
              The company we are using to perform compliance checks against sanctions
            </li>
            <li>
              The vendors we use to securely store all data, including personal data
            </li>
          </List>

          <SubHeader>
            10.2. How do you protect my data when transferred internationally?
          </SubHeader>
          <List>
            <li>
              First and most importantly, by ensuring that we only work with vendors and
              partners that treat data securely.
            </li>
            <li>
              We review our vendors and partners each year to ensure they maintain strong
              security standards.
            </li>
            <li>
              We only transfer personal data to countries that we have assessed and
              concluded that they have sufficient data protection frameworks.
            </li>
            <li>
              We put the necessary standard contractual clauses (as per Data Privacy
              regulations) in our contracts with third parties to ensure that they will do
              what the data protection law requires to provide us with adequate data
              privacy and security levels.
            </li>
          </List>
        </Section>

        <Section>
          <Heading>11. How does SafetyWing make sure my personal data is secure?</Heading>
          <List>
            We treat your data like ours. And for this reason we take the security of
            personal data seriously and have the necessary measures in place:
            <li>
              We keep all of our data including your personal data collected on secure
              cloud locations (Google Cloud Platform).
            </li>
            <li>
              We ensure that only a limited number of people have access to your personal
              data, on a need-to-know basis.
            </li>
            <li>
              We review our data privacy and security infrastructure frequently, using
              independent audits to ensure that we are keeping up with global regulatory
              requirements and best practices.
            </li>
            <li>
              We have procedures in place in case of a data breach, which include
              notifying the affected users where relevant.
            </li>
          </List>
        </Section>

        <Section>
          <Heading>
            12. Do I have any rights on the processing of my data (“Data Subjects
            rights”)?
          </Heading>
          <Paragraph>Of course you do, and we explain everything below.</Paragraph>
          <Paragraph>
            You can exercise your rights by sending an email to{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or reaching
            out to our Customer Care team through our website.
          </Paragraph>
          <Paragraph>
            We always try to address your request <b>within 1 month of receiving them</b>,
            and in the event we will need more time, we will let you know.
          </Paragraph>
          <SubHeader>
            12.1. First of all, you have the right to know what personal data we are
            holding related to you (“Right of access”).
          </SubHeader>
          <Paragraph>
            When you submit a Subject Access Request, we will tell you what personal data
            we have about you, whether these are transferred to third parties, the
            duration we intend to keep the data in our records, whether we use your data
            to perform automated profiling, and other details explicitly related to you.
          </Paragraph>
          <Paragraph>
            Just keep in mind that we <b>cannot</b> execute a request that does not have
            reasonable grounds, or one that is regarding a person other than you.
          </Paragraph>
          <SubHeader>
            12.2 You can request that we update your personal data if it is not accurate
            (“Right of rectification”).
          </SubHeader>
          <Paragraph>
            Do you believe that personal data we have related to you is not correct? Just
            let us know and we will explain the process to update everything to be
            correct.
          </Paragraph>
          <SubHeader>
            12.3. You can ask for your personal data to be deleted (“Right of erasure”).
          </SubHeader>
          <Paragraph>
            You can request that we delete some or all of your data, and we will gladly
            comply, to the best of our abilities.
          </Paragraph>
          <Paragraph>
            While you have the right to request for your data to be deleted, we also have
            a legal obligation to keep the data for a certain period of time (“retention
            schedule”). When we receive such a request from you, we will let you know what
            data we can delete now, and what data we will need to wait to delete.
          </Paragraph>
          <SubHeader>
            12.4. You can request that we restrict the processing of your personal data
            (“Right to restriction of processing”).
          </SubHeader>
          <Paragraph>
            If you would like us to stop processing your personal data any further
            (excluding the storing of your data, as this is still required), you can
            request this. We will review your request and if our legal obligations allow
            us to execute your request, we will happily do so.
          </Paragraph>
          <SubHeader>
            12.5. You can ask that we send your personal data to another third party
            (“Right to data portability”).
          </SubHeader>
          <Paragraph>
            According to this right, you can ask that we send any of your personal data we
            keep in a structured, commonly used and machine-readable format to another
            Data Controller.
          </Paragraph>
          <SubHeader>
            12.6. You have the right to request that we do not make any automated
            decisions based on your personal data (“Right to object to automated
            individual decision-making”).
          </SubHeader>
          <Paragraph>
            In cases where your personal data are used to make automated decisions, you
            have the right to object to it and the process will stop, unless there are
            other pressing reasons, which will be communicated to you.
          </Paragraph>
          <Paragraph>
            As mentioned above, you can exercise your rights or ask more information about
            them by sending an email to{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or
            contacting us through our 24/7 customer care chat through our website.
          </Paragraph>
        </Section>

        <Section>
          <Heading>13. How long do you keep my data for (“Retention schedule”)?</Heading>
          <Paragraph>Only for as long as we need to, and not a day more.</Paragraph>
          <List>
            We keep data as long as laws and regulations require us to. Some of the
            factors we take into account to decide on how long to hold your data
            (“retention period”) are:
            <li>
              Customer expectations, the nature of your relationship with us, your
              membership status and the types of accounts, products and services you have
              with us.
            </li>
            <li>
              The maximum or minimum retention periods identified by legal or regulatory
              guidance.
            </li>
            <li>Our contractual rights and obligations.</li>
            <li>
              Forensic requirements, for example, the need to access data no longer
              actively used in order to manage or respond to a complaint or dispute.
            </li>
            <li>
              The risks involved in retention, deletion and removal and cost of
              maintaining, storing, archiving and retrieving data.
            </li>
            <Paragraph>
              You can see below a more detailed presentation of how long we keep different
              types of data:
            </Paragraph>
          </List>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                <li>Name</li>
                <li>Title (e.g. Ms, Mr)</li>
                <li>Sex</li>
                <li>Address</li>
                <li>Date of birth</li>
                <li>Nationality</li>
                <li>Occupation</li>
                <li>
                  National identifier numbers, passport numbers, driver's license number
                </li>
                <li>Email address and phone number</li>
                <li>Family details, marital status, next of kin, dependents</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>7 years after the end of our business relationship.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Call recordings</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>3 years after the end of our business relationship.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <List>
                <li>Geo-location data</li>
                <li>How you interact with us</li>
              </List>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>1 year after the end of our business relationship.</Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Direct marketing preferences</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>
                Until you tell us you no longer wish to receive marketing.
              </Paragraph>
            </BoxColumn>
          </Box>
          <Box>
            <BoxColumn>
              <b>Data type</b>
              <Paragraph>Enquiries only</Paragraph>
            </BoxColumn>
            <BoxColumn>
              <b>How long we keep it for</b>
              <Paragraph>
                Personal Data of people that expressed enquiries without making any
                purchases are usually kept for 3 months.
              </Paragraph>
            </BoxColumn>
          </Box>
        </Section>

        <Section>
          <Heading>14. Phew, that's it!</Heading>
          <Paragraph>
            So, that was our Privacy Policy. Thank you for reading through all of it!
          </Paragraph>
          <Paragraph>
            We really hope that we have answered any questions you might have had, and
            have given you enough information to understand both what we do with your
            personal data as well as how we do it.
          </Paragraph>
          <Paragraph>
            If you still have questions or concerns, please reach out to our Data
            Protection Officer by email on{' '}
            <Email href="mailto:dpo@safetywing.com">dpo@safetywing.com</Email> or contact
            us via our 24/7 live chat.
          </Paragraph>
        </Section>
      </Background>
    </Container>
  );
};

export default PrivacyPolicy;
